import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { applyRowMasksToData } from 'services/datachecks/datachecks';
import { setIsBigMessage, setUiConfig, useWebEDIStore } from 'stores/webedi/webedi';
import { CANCELLED_ERROR } from 'support/helpers/hooks/useDebouncePromise';
import { useDebouncedWatch } from 'support/helpers/hooks/useDebounceWatch';
import { isBigMessage } from 'support/helpers/messages/messages';
import { DocumentType, ValueOf } from 'support/types';

import { useInitialWebEDISetup } from './hooks/useInitialWebEDISetup';
import { getEnrichedFlattenDataAndUIConfig } from './getEnrichedFlattenDataAndUIConfig';

export type DatachecksProps = {
  targetRelationshipId?: string;
  documentType: ValueOf<DocumentType> | undefined;
};

export const DEBOUNCE_TIME = 800;
export const Datachecks = ({ targetRelationshipId, documentType }: DatachecksProps) => {
  useInitialWebEDISetup();

  const processSpecificationFields = useWebEDIStore((state) => state.processSpecificationFields);
  const {
    watch,
    reset,
    trigger,
    formState: { isSubmitted },
  } = useFormContext();

  const documentFlattenData = useDebouncedWatch(watch, DEBOUNCE_TIME);

  // Process form data changes
  useEffect(() => {
    const processData = async () => {
      if (!documentFlattenData || Object.keys(documentFlattenData).length === 0 || !documentType) {
        return;
      }

      try {
        const result = await getEnrichedFlattenDataAndUIConfig({
          data: documentFlattenData,
          receiverProcessSpecificationFields: processSpecificationFields,
          documentType,
        });

        if (!result) {
          return;
        }

        const { data, uiConfig } = result;
        const filteredData = uiConfig ? applyRowMasksToData(data, uiConfig) : data;

        const isLargeMessage = isBigMessage(filteredData ?? undefined);
        setIsBigMessage(isLargeMessage);

        reset(filteredData ?? undefined, {
          keepIsSubmitted: true,
          keepErrors: true,
          keepDirty: true,
        });

        if (isSubmitted && !isLargeMessage) {
          trigger();
        }

        setUiConfig(uiConfig);
      } catch (error) {
        if (error instanceof Error && error.message !== CANCELLED_ERROR) {
          console.error('Error processing form data:', error);
        }
      }
    };

    processData();
  }, [
    documentType,
    processSpecificationFields,
    targetRelationshipId,
    documentFlattenData,
    reset,
    trigger,
    isSubmitted,
  ]);

  return null;
};
